// Where the book plays: this device, a Google Cast device or AirPlay. One
// view of `cast.ts` and `airplay.ts` for the UI's cast button.

import { useStore } from '../lib/store';
import { airPlayStore, initAirPlay, type AirPlayStatus } from './airplay';
import { castStore, initCast, type CastStatus } from './cast';
import { player } from './engine';

export interface PlaybackOutput {
  /** The kind of device the button offers: Cast (Chromium), AirPlay (Safari), or none. */
  kind: 'cast' | 'airplay' | null;
  /** The book plays on another device. */
  connected: boolean;
  connecting: boolean;
  /** The receiver's name (Cast only: AirPlay does not tell the page). */
  device: string | null;
}

/** The cast button's state from the two stores. AirPlay wins on WebKit, where Cast never loads. */
export function playbackOutput(cast: { status: CastStatus; device: string | null }, airplay: { status: AirPlayStatus }): PlaybackOutput {
  if (airplay.status === 'connected') return { kind: 'airplay', connected: true, connecting: false, device: null };
  if (cast.status === 'connected' || cast.status === 'connecting') {
    return { kind: 'cast', connected: cast.status === 'connected', connecting: cast.status === 'connecting', device: cast.device };
  }
  if (airplay.status === 'available') return { kind: 'airplay', connected: false, connecting: false, device: null };
  if (cast.status === 'available' || cast.status === 'noDevices') return { kind: 'cast', connected: false, connecting: false, device: null };
  return { kind: null, connected: false, connecting: false, device: null };
}

export function usePlaybackOutput(): PlaybackOutput {
  const cast = useStore(castStore);
  const airplay = useStore(airPlayStore);
  return playbackOutput(cast, airplay);
}

/** Starts Cast discovery (Chromium) and AirPlay (Safari). Idempotent; call once signed in. */
export function initPlaybackOutputs(): void {
  initAirPlay(player.getAudioElement());
  initCast(player);
}
